// Verträge des Eingangs (SPEZIFIKATION §6, §10.2, AP6): hochladen, lesen lassen, Lieferant und Positionen zuordnen,
// mit Bestellung und Wareneingang abgleichen, als Eingangsrechnung bzw. Lieferantengutschrift erfassen, ablehnen.
// Eingangsrechnungen bestehen aus Einkaufspreisen – jede Aktion verlangt zusätzlich „Einstandspreise sehen“ (im Handler).
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { zListPage } from '@shared/list'
import { z } from 'zod'
import { zDay, zFinding } from './common'

export const INBOUND_READ = 'einvoice.inbound.read'
export const INBOUND_IMPORT = 'einvoice.inbound.import'
export const INBOUND_PROCESS = 'einvoice.inbound.process'

// ---- Zustände und Bezeichnungen -----------------------------------------------------------------------------------

export const INBOUND_STATUSES = ['received', 'parsed', 'failed', 'assigned', 'captured', 'rejected'] as const
export type InboundStatus = (typeof INBOUND_STATUSES)[number]
export const INBOUND_STATUS_LABELS: Record<InboundStatus, string> = {
  received: 'Eingegangen – wird gelesen',
  parsed: 'Gelesen – Lieferant zuordnen',
  failed: 'Nicht lesbar',
  assigned: 'Lieferant zugeordnet',
  captured: 'Erfasst',
  rejected: 'Abgelehnt',
}

/** Ergebnis einer Leseversion (§6.9); `unsupported` und `rejected` seit Migration 2201 */
export const INBOUND_OUTCOMES = [
  'ok',
  'warnings',
  'errors',
  'not_einvoice',
  'unreadable',
  'unsupported',
  'rejected',
] as const
export type InboundOutcome = (typeof INBOUND_OUTCOMES)[number]
export const INBOUND_OUTCOME_LABELS: Record<InboundOutcome, string> = {
  ok: 'Ohne Befund',
  warnings: 'Mit Hinweisen',
  errors: 'Mit Fehlern',
  not_einvoice: 'Keine E-Rechnung',
  unreadable: 'Formatfehler',
  unsupported: 'Nicht unterstützt',
  rejected: 'Abgelehnt (Sicherheitsgrenze)',
}

export const SUPPLIER_MATCHES = ['vat_id', 'tax_number', 'gln', 'iban', 'manual'] as const
export type SupplierMatch = (typeof SUPPLIER_MATCHES)[number]
export const SUPPLIER_MATCH_LABELS: Record<SupplierMatch, string> = {
  vat_id: 'über die USt-IdNr.',
  tax_number: 'über die Steuernummer',
  gln: 'über die GLN',
  iban: 'über die IBAN',
  manual: 'von Hand',
}

export const REJECT_REASONS = ['duplicate', 'not_addressed_to_us', 'not_einvoice', 'other'] as const
export type RejectReason = (typeof REJECT_REASONS)[number]
export const REJECT_REASON_LABELS: Record<RejectReason, string> = {
  duplicate: 'Doppelt eingegangen',
  not_addressed_to_us: 'Nicht an uns adressiert',
  not_einvoice: 'Keine E-Rechnung',
  other: 'Anderer Grund',
}

/** Wie eine Position der E-Rechnung in Merxo ankommt */
export const LINE_ROLES = ['goods', 'return', 'deposit', 'empties', 'cost', 'unmapped'] as const
export type LineRole = (typeof LINE_ROLES)[number]
export const LINE_ROLE_LABELS: Record<LineRole, string> = {
  goods: 'Ware',
  return: 'Warenrücknahme',
  deposit: 'Pfand',
  empties: 'Leergut-Gutschrift',
  cost: 'Kostenzeile',
  unmapped: 'Nicht zugeordnet',
}

/** Woher die Zuordnung einer Position stammt (§6.6: gelernt → Lieferanten-Artikelnummer → GTIN → unsere Nummer) */
export const MAPPING_SOURCES = ['manual', 'alias', 'supplier_sku', 'gtin', 'buyer_id'] as const
export type MappingSource = (typeof MAPPING_SOURCES)[number]
export const MAPPING_SOURCE_LABELS: Record<MappingSource, string> = {
  manual: 'von Hand',
  alias: 'gelernt',
  supplier_sku: 'Artikelnummer des Lieferanten',
  gtin: 'GTIN',
  buyer_id: 'unsere Artikelnummer',
}

// ---- Bausteine ----------------------------------------------------------------------------------------------------

const zRef = z.object({ id: zId, number: z.string().nullable(), name: z.string() })
export const zDocumentRef = z.object({
  id: zId,
  type: z.string(),
  typeTitle: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
})

export const zInboundRow = z.object({
  id: zId,
  source: z.enum(['upload', 'email', 'peppol']),
  status: z.enum(INBOUND_STATUSES),
  statusLabel: z.string(),
  receivedAt: z.date(),
  receivedByName: z.string().nullable(),
  /** Dateiname beim Hochladen (null nach dem Löschkonzept) */
  originalName: z.string().nullable(),
  originalMime: z.string(),
  originalFileId: zId,
  sha256: z.string(),
  outcome: z.enum(INBOUND_OUTCOMES).nullable(),
  outcomeLabel: z.string().nullable(),
  /** ein Satz zum Leseergebnis */
  message: z.string().nullable(),
  /** Format: „XRechnung 3.0 (UBL)“, „ZUGFeRD/Factur-X (PDF)“ … */
  formatLabel: z.string().nullable(),
  typeCode: z.string().nullable(),
  typeLabel: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  issueDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  currency: z.string().nullable(),
  /** BT-27 (null nach dem Löschkonzept) */
  sellerName: z.string().nullable(),
  supplier: zRef.nullable(),
  supplierMatch: z.enum(SUPPLIER_MATCHES).nullable(),
  supplierMatchLabel: z.string().nullable(),
  grossCents: z.int().nullable(),
  taxCents: z.int().nullable(),
  payableCents: z.int().nullable(),
  document: zDocumentRef.nullable(),
  duplicateOfId: zId.nullable(),
  rejectReason: z.enum(REJECT_REASONS).nullable(),
  rejectReasonLabel: z.string().nullable(),
  rejectNote: z.string().nullable(),
  version: z.int(),
})
export type InboundRow = z.output<typeof zInboundRow>

// ---- Lesbare Ansicht (§6.8) – Daten, keine Oberfläche ------------------------------------------------------------------

/** Ein Feld mit BT-Nummer (für den Tooltip), deutscher Bezeichnung und Wert (Datum ISO, Zahl als Dezimaltext) */
export const zViewField = z.object({ bt: z.string(), label: z.string(), value: z.string() })

export const zViewParty = z.object({
  role: z.enum(['seller', 'buyer', 'payee', 'taxRepresentative', 'delivery']),
  title: z.string(),
  fields: z.array(zViewField),
})

export const zViewAllowanceCharge = z.object({
  charge: z.boolean(),
  /** Abschlag bzw. Zuschlag */
  label: z.string(),
  amountCents: z.int().nullable(),
  baseCents: z.int().nullable(),
  percent: z.string().nullable(),
  vatCategory: z.string().nullable(),
  vatRate: z.string().nullable(),
  reason: z.string().nullable(),
  reasonCode: z.string().nullable(),
})

export const zViewLine = z.object({
  /** Schlüssel der Zuordnung (BT-126, sonst die laufende Nummer) */
  key: z.string(),
  pos: z.int(),
  /** BT-126 */
  id: z.string().nullable(),
  /** BT-153, BT-154 */
  name: z.string().nullable(),
  description: z.string().nullable(),
  /** BT-155, BT-156, BT-157 */
  sellerId: z.string().nullable(),
  buyerId: z.string().nullable(),
  gtin: z.string().nullable(),
  /** BT-129 als Dezimaltext mit Vorzeichen, BT-130 und seine Bezeichnung */
  quantity: z.string().nullable(),
  unitCode: z.string().nullable(),
  unitLabel: z.string().nullable(),
  /** BT-146, BT-149, BT-148 */
  netPrice: z.string().nullable(),
  baseQuantity: z.string().nullable(),
  grossPrice: z.string().nullable(),
  /** BT-131 */
  netCents: z.int().nullable(),
  /** BT-151, BT-152 */
  vatCategory: z.string().nullable(),
  vatRate: z.string().nullable(),
  allowancesCharges: z.array(zViewAllowanceCharge),
  /** BT-127 unverändert */
  note: z.string().nullable(),
  /** BG-26 */
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  orderLineReference: z.string().nullable(),
  attributes: z.array(z.object({ name: z.string().nullable(), value: z.string().nullable() })),
  /** Ware, Warenrücknahme (negative Menge), Pfand, Leergut – aus der Zuordnung bzw. dem Vorzeichen */
  role: z.enum(LINE_ROLES),
  roleLabel: z.string(),
})

export const zViewTax = z.object({
  category: z.string().nullable(),
  categoryLabel: z.string().nullable(),
  rate: z.string().nullable(),
  taxableCents: z.int().nullable(),
  taxCents: z.int().nullable(),
  exemptionReason: z.string().nullable(),
  exemptionCode: z.string().nullable(),
})

export const zViewAttachment = z.object({
  position: z.int(),
  /** BT-122, BT-123 */
  reference: z.string().nullable(),
  description: z.string().nullable(),
  filename: z.string().nullable(),
  mimeCode: z.string().nullable(),
  sizeBytes: z.int(),
  sha256: z.string(),
  /** abgelegte Datei zum Herunterladen; null = Dateityp nicht ablegbar, nur im Original enthalten */
  fileId: zId.nullable(),
  url: z.string().nullable(),
})

export const zReadableInvoice = z.object({
  header: z.array(zViewField),
  parties: z.array(zViewParty),
  lines: z.array(zViewLine),
  allowancesCharges: z.array(zViewAllowanceCharge),
  taxes: z.array(zViewTax),
  /** BG-22 – Cent, null = fehlt in der Datei */
  totals: z.object({
    lineNetCents: z.int().nullable(),
    allowancesCents: z.int().nullable(),
    chargesCents: z.int().nullable(),
    netCents: z.int().nullable(),
    taxCents: z.int().nullable(),
    grossCents: z.int().nullable(),
    prepaidCents: z.int().nullable(),
    roundingCents: z.int().nullable(),
    payableCents: z.int().nullable(),
  }),
  payment: z.object({
    fields: z.array(zViewField),
    /** BT-20 genau wie in der Datei – und strukturiert */
    termsText: z.string().nullable(),
    termsDescription: z.string().nullable(),
    discounts: z.array(
      z.object({ days: z.int(), endDate: z.string().nullable(), percentBp: z.int(), basisCents: z.int().nullable() }),
    ),
    termsNote: z.string().nullable(),
  }),
  notes: z.array(z.object({ subjectCode: z.string().nullable(), content: z.string() })),
  precedingInvoices: z.array(z.object({ id: z.string().nullable(), issueDate: z.string().nullable() })),
  attachments: z.array(zViewAttachment),
})
export type ReadableInvoice = z.output<typeof zReadableInvoice>

export const zParseVersion = z.object({
  id: zId,
  createdAt: z.date(),
  parserVersion: z.string(),
  outcome: z.enum(INBOUND_OUTCOMES),
  outcomeLabel: z.string(),
  message: z.string().nullable(),
  xmlFileId: zId.nullable(),
  xmlSha256: z.string().nullable(),
})

export const zInboundView = zInboundRow.extend({
  /** Leseversionen, neueste zuerst */
  versions: z.array(zParseVersion),
  /** extrahierte XML einer PDF bzw. die XML selbst */
  xmlFileId: zId.nullable(),
  /** Prüfbefunde der neuesten Leseversion mit Regel-ID und BT */
  findings: z.array(zFinding),
  /** Rechnung nicht ordnungsgemäß (Pflichtangabe nach §§ 14 Abs. 4, 14a UStG fehlt) – beim Lieferanten Berichtigung anfordern */
  improper: z.boolean(),
  /** ist die Rechnung an uns adressiert (BT-48 = unsere USt-IdNr. oder BT-44 ≈ Firmenname)? null = nicht feststellbar */
  addressedToUs: z.boolean().nullable(),
  /** andere Eingänge bzw. erfasste Belege mit derselben Rechnungsnummer desselben Lieferanten */
  duplicates: z.array(z.object({ inboundId: zId.nullable(), document: zDocumentRef.nullable(), message: z.string() })),
  invoice: zReadableInvoice.nullable(),
})
export type InboundView = z.output<typeof zInboundView>

// ---- Abgleich (Vorschlag) ----------------------------------------------------------------------------------------------

export const zMatchProblem = z.object({
  /** Schlüssel der Position bzw. des Ab-/Zuschlags; null = die ganze Rechnung */
  key: z.string().nullable(),
  /** sperrt das Erfassen */
  blocking: z.boolean(),
  message: z.string(),
})

export const zMatchLineProposal = z.object({
  key: z.string(),
  pos: z.int(),
  name: z.string().nullable(),
  sellerId: z.string().nullable(),
  gtin: z.string().nullable(),
  quantity: z.string().nullable(),
  unitCode: z.string().nullable(),
  netCents: z.int().nullable(),
  role: z.enum(LINE_ROLES),
  roleLabel: z.string(),
  mapping: z
    .object({
      source: z.enum(MAPPING_SOURCES),
      sourceLabel: z.string(),
      itemId: zId.nullable(),
      itemName: z.string().nullable(),
      itemUnitId: zId.nullable(),
      unit: z.string().nullable(),
      depositTypeId: zId.nullable(),
      depositName: z.string().nullable(),
    })
    .nullable(),
  /** Menge in Tausendstel des Gebindes (Betrag) */
  qty: z.int().nullable(),
  /** Preis wie erfasst: Cent netto je Preiseinheit (Tausendstel), Zeilenrabatt in Basispunkten */
  price: z.object({ unitPriceCents: z.int(), pricePerQty: z.int(), discountBp: z.int() }).nullable(),
  /** Zeile des Wareneingangs bzw. der Bestellung, der die Position zugeordnet ist */
  sourceLine: z.object({ documentId: zId, number: z.string().nullable(), lineId: zId }).nullable(),
  taxCode: z.object({ id: zId, label: z.string() }).nullable(),
  problems: z.array(zMatchProblem),
})

export const zMatchAllowanceProposal = z.object({
  key: z.string(),
  charge: z.boolean(),
  reason: z.string().nullable(),
  amountCents: z.int().nullable(),
  vatCategory: z.string().nullable(),
  vatRate: z.string().nullable(),
  /** gemeinsamer Belegrabatt (headerDiscountBp) statt einer Abzugszeile */
  asHeaderDiscount: z.boolean(),
  taxCode: z.object({ id: zId, label: z.string() }).nullable(),
})

export const zSourceProposal = z.object({
  id: zId,
  type: z.string(),
  typeTitle: z.string(),
  number: z.string().nullable(),
  documentDate: z.string(),
  externalRef: z.string().nullable(),
  locationId: zId,
  /** wie gefunden: BT-16 (Lieferschein), BT-15 (unsere WE-Nummer), BT-13 (Bestellung) */
  via: z.enum(['despatch_advice', 'receiving_advice', 'order']),
  viaLabel: z.string(),
})

export const zTotalsComparison = z.object({
  rate: z.string().nullable(),
  category: z.string().nullable(),
  /** Steuerbasis und Steuer laut E-Rechnung (BT-116, BT-117) und laut Beleg */
  einvoiceNetCents: z.int(),
  einvoiceTaxCents: z.int(),
  documentNetCents: z.int(),
  documentTaxCents: z.int(),
  deltaNetCents: z.int(),
  deltaTaxCents: z.int(),
})

export const zInboundMatch = z.object({
  id: zId,
  status: z.enum(INBOUND_STATUSES),
  /** wie die E-Rechnung erfasst würde: Eingangsrechnung, Lieferantengutschrift – oder gar nicht */
  target: z.enum(['invoice', 'credit_note']).nullable(),
  targetLabel: z.string().nullable(),
  supplier: zRef.nullable(),
  supplierMatch: z.enum(SUPPLIER_MATCHES).nullable(),
  /** Vorschläge per Name und Postleitzahl (nie automatisch zugeordnet) */
  supplierSuggestions: z.array(zRef.extend({ score: z.number() })),
  addressedToUs: z.boolean().nullable(),
  order: z.object({ id: zId, number: z.string().nullable() }).nullable(),
  sources: z.array(zSourceProposal),
  /** Vorschlag für sourceIds von einvoice.inbound.capture */
  proposedSourceIds: z.array(zId),
  /** Standort: der des ersten Belegs, sonst der der bezogenen Rechnung (Gutschrift) – sonst zu wählen */
  locationId: zId.nullable(),
  /** Gutschrift: unsere Eingangsrechnung, die sie mindert (aus BG-3) */
  invoice: zDocumentRef.nullable(),
  headerDiscountBp: z.int(),
  lines: z.array(zMatchLineProposal),
  allowancesCharges: z.array(zMatchAllowanceProposal),
  problems: z.array(zMatchProblem),
  /** lässt sich erfassen (keine sperrende Lücke) */
  capturable: z.boolean(),
  /** nach dem Erfassen: Summen je Steuersatz der E-Rechnung gegen den Beleg – jede Abweichung steht hier */
  comparison: z.array(zTotalsComparison),
  /** ein Satz zum Stand */
  message: z.string(),
})
export type InboundMatch = z.output<typeof zInboundMatch>

// ---- Verträge -----------------------------------------------------------------------------------------------------------

const zNote = z.string().trim().max(500, { error: 'Die Notiz ist zu lang (höchstens 500 Zeichen).' })

export const inboundImport = defineContract({
  name: 'einvoice.inbound.import',
  title: 'E-Rechnungen einlesen',
  description:
    'Übernimmt hochgeladene Dateien (XRechnung, ZUGFeRD/Factur-X-PDF) in den Eingang und lässt sie lesen und prüfen.',
  llm: {
    description:
      'Nur für die Oberfläche: hochgeladene Dateien (POST /api/files?kind=einvoice) in den Eingang übernehmen.',
    keywords: [],
  },
  input: z.strictObject({
    fileIds: z
      .array(zId)
      .min(1, { error: 'Bitte wählen Sie mindestens eine Datei.' })
      .max(20, { error: 'Höchstens 20 Dateien auf einmal.' }),
  }),
  output: z.object({
    items: z.array(
      z.object({
        fileId: zId,
        inboundId: zId,
        /** dieselbe Datei war schon eingelesen – kein neuer Eingang */
        duplicate: z.boolean(),
        status: z.enum(INBOUND_STATUSES),
        message: z.string(),
      }),
    ),
  }),
  permission: INBOUND_IMPORT,
  risk: 'write',
  assistant: 'never',
  limits: { maxItems: 20, itemCount: (input) => input.fileIds.length },
})

export const inboundList = defineContract({
  name: 'einvoice.inbound.list',
  title: 'Eingegangene E-Rechnungen',
  description: 'Eingegangene E-Rechnungen mit Leseergebnis, Lieferant und Stand – neueste zuerst.',
  llm: {
    description:
      'Listet eingegangene E-Rechnungen von Lieferanten (hochgeladen): Stand (gelesen, Lieferant zugeordnet, erfasst, abgelehnt), Leseergebnis (ohne Befund, mit Fehlern, keine E-Rechnung …), Rechnungsnummer, Lieferant, Beträge. Filter: Stand, Lieferant, Zeitraum des Eingangs, nur offene.',
    keywords: [
      'E-Rechnungen Eingang',
      'eingegangene Rechnungen',
      'XRechnung vom Lieferanten',
      'ZUGFeRD',
      'Posteingang Rechnungen',
    ],
  },
  input: z.strictObject({
    status: z.array(z.enum(INBOUND_STATUSES)).max(6).optional(),
    /** nur, was noch zu tun ist (nicht erfasst, nicht abgelehnt) */
    open: z.boolean().default(false),
    supplierId: zId.optional(),
    from: zDay.optional(),
    to: zDay.optional(),
    page: zListPage,
  }),
  output: z.object({ rows: z.array(zInboundRow), nextCursor: z.string().nullable() }),
  permission: INBOUND_READ,
  risk: 'read',
})

export const inboundGet = defineContract({
  name: 'einvoice.inbound.get',
  title: 'Eingegangene E-Rechnung anzeigen',
  description:
    'Eine eingegangene E-Rechnung in lesbarer Form: Kopf, Parteien, Positionen, Steuern, Summen, Zahlung, Anlagen, Prüfbefunde.',
  llm: {
    description:
      'Liefert eine eingegangene E-Rechnung lesbar aufbereitet (Kopf, Verkäufer und Käufer, Positionen mit Gebindewerten und Rücknahmen, Ab- und Zuschläge, Steuertabelle, Summen, Zahlung mit Skonto, Anlagen) samt Prüfbefunden mit Regel-ID, Leseversionen und Dubletten. Verändert nichts.',
    keywords: ['E-Rechnung anzeigen', 'XRechnung lesen', 'Rechnung des Lieferanten', 'Prüfbefund'],
  },
  input: z.strictObject({ id: zId }),
  output: zInboundView,
  permission: INBOUND_READ,
  risk: 'read',
})

export const inboundAssignSupplier = defineContract({
  name: 'einvoice.inbound.assignSupplier',
  title: 'Lieferant einer E-Rechnung zuordnen',
  description:
    'Ordnet einer eingegangenen E-Rechnung den Lieferanten von Hand zu, wenn er nicht eindeutig erkannt wurde.',
  llm: {
    description:
      'Ordnet einer eingegangenen E-Rechnung einen Lieferanten zu (wenn USt-IdNr., Steuernummer, GLN und IBAN keinen eindeutigen Treffer ergaben). Nur vor dem Erfassen.',
    keywords: ['Lieferant zuordnen', 'E-Rechnung zuordnen'],
  },
  input: z.strictObject({ id: zId, supplierId: zId }),
  output: zInboundRow,
  permission: INBOUND_PROCESS,
  risk: 'write',
  assistant: 'opt-in',
})

export const inboundMapLine = defineContract({
  name: 'einvoice.inbound.mapLine',
  title: 'Position einer E-Rechnung zuordnen',
  description:
    'Ordnet eine Position der E-Rechnung einem Gebinde eines Artikels, einer Gebindeart oder einer Kostenzeile zu – auf Wunsch gelernt für künftige Rechnungen desselben Lieferanten.',
  llm: {
    description:
      'Ordnet eine Position (lineId = BT-126 bzw. „ac:1“ für den ersten Ab-/Zuschlag) zu: `itemUnitId` (Gebinde eines Artikels – auch „Einheit zuordnen“), `depositTypeId` (Gebindewert bzw. zurückgegebene Gebinde), `cost: true` (Kostenzeile ohne Artikel) oder nur `taxCodeId` (Steuerschlüssel). `remember` lernt die Zuordnung für diesen Lieferanten (Artikelnummer bzw. GTIN + Einheit). `clear` nimmt die Zuordnung zurück.',
    keywords: ['Position zuordnen', 'Artikel zuordnen', 'Einheit zuordnen', 'Gebindeart zuordnen'],
  },
  input: z
    .strictObject({
      id: zId,
      lineId: z.string().trim().min(1).max(120),
      itemUnitId: zId.optional(),
      depositTypeId: zId.optional(),
      cost: z.boolean().optional(),
      taxCodeId: zId.optional(),
      clear: z.boolean().optional(),
      /** für künftige Rechnungen dieses Lieferanten merken (nur Gebinde und Pfandart) */
      remember: z.boolean().default(false),
    })
    .refine(
      (input) =>
        [
          input.itemUnitId !== undefined,
          input.depositTypeId !== undefined,
          input.cost === true,
          input.clear === true,
        ].filter(Boolean).length <= 1,
      {
        error: 'Bitte wählen Sie genau eines: Gebinde, Pfandart, Kostenzeile oder zurücknehmen.',
        path: ['itemUnitId'],
      },
    )
    .refine(
      (input) =>
        input.clear === true ||
        input.itemUnitId !== undefined ||
        input.depositTypeId !== undefined ||
        input.cost === true ||
        input.taxCodeId !== undefined,
      {
        error: 'Bitte wählen Sie ein Gebinde, eine Pfandart, eine Kostenzeile oder einen Steuerschlüssel.',
        path: ['itemUnitId'],
      },
    )
    .refine((input) => !input.remember || input.itemUnitId !== undefined || input.depositTypeId !== undefined, {
      error: 'Merken lässt sich nur die Zuordnung zu einem Gebinde oder einer Pfandart.',
      path: ['remember'],
    }),
  output: zInboundMatch,
  permission: INBOUND_PROCESS,
  risk: 'write',
  assistant: 'opt-in',
})

export const inboundMatchContract = defineContract({
  name: 'einvoice.inbound.match',
  title: 'E-Rechnung mit Bestellung und Wareneingang abgleichen',
  description:
    'Schlägt vor, wie die E-Rechnung erfasst wird: Lieferant, Bestellung, Wareneingänge, je Position Artikel, Menge, Preis und Steuerschlüssel – mit allen Lücken.',
  llm: {
    description:
      'Liefert den Vorschlag zum Erfassen einer eingegangenen E-Rechnung: erkannter Lieferant, Bestellung (BT-13) und Wareneingänge (BT-16/BT-15), je Position die Zuordnung (gelernt, Artikelnummer des Lieferanten, GTIN, unsere Artikelnummer), Menge, Preis, Zeile des Wareneingangs und Steuerschlüssel; Belegrabatt und Zusatzzeilen; Lücken, die das Erfassen sperren. Nach dem Erfassen zusätzlich die Summen je Steuersatz gegen den Beleg. Verändert nichts.',
    keywords: ['E-Rechnung abgleichen', 'Wareneingang zuordnen', 'Bestellung zuordnen', 'Rechnungsprüfung'],
  },
  input: z.strictObject({ id: zId }),
  output: zInboundMatch,
  permission: INBOUND_PROCESS,
  risk: 'read',
})

export const inboundCapture = defineContract({
  name: 'einvoice.inbound.capture',
  title: 'E-Rechnung als Eingangsrechnung erfassen',
  description:
    'Legt aus der E-Rechnung den Entwurf einer Eingangsrechnung (bzw. Lieferantengutschrift) an – geprüft und freigegeben wird im Einkauf.',
  llm: {
    description:
      'Erfasst eine eingegangene E-Rechnung im Einkauf als Entwurf (Eingangsrechnung „In Prüfung“ bzw. bei Typ 381 eine Lieferantengutschrift) – mit den Rechten der Person, über purchasing.invoice.capture bzw. purchasing.creditNote.capture. `sourceIds` (Wareneingänge/Bestellungen) und `locationId` ohne Angabe wie im Vorschlag (einvoice.inbound.match); `lines` legt je Position die Zeile des Wareneingangs fest (null = ohne Bezug) bzw. bei Gutschriften Rücknahme oder Preisnachlass; `unmappedAsCost` übernimmt nicht zugeordnete Positionen als Kostenzeilen. Gebucht und freigegeben wird nichts.',
    keywords: ['E-Rechnung erfassen', 'Eingangsrechnung anlegen', 'XRechnung übernehmen'],
  },
  input: z.strictObject({
    id: zId,
    sourceIds: z.array(zId).max(50).optional(),
    locationId: zId.optional(),
    lines: z
      .array(
        z.strictObject({
          lineId: z.string().trim().min(1).max(120),
          /** Zeile des Wareneingangs bzw. der Bestellung; null = ohne Bezug */
          sourceLineId: zId.nullable().optional(),
          /** nur Gutschrift: Ware zurück (mit Pfand) oder Preisnachlass (ohne Pfand) */
          reason: z.enum(['return', 'price']).optional(),
        }),
      )
      .max(500)
      .optional(),
    /** nicht zugeordnete Positionen als Kostenzeilen (ohne Artikel) übernehmen */
    unmappedAsCost: z.boolean().default(false),
    notes: z.string().trim().max(2000).nullish(),
  }),
  output: z.object({ inbound: zInboundRow, documentId: zId, documentType: z.string(), match: zInboundMatch }),
  permission: INBOUND_PROCESS,
  risk: 'write',
  limits: { maxItems: 500, itemCount: (input) => input.lines?.length ?? 1 },
})

export const inboundReject = defineContract({
  name: 'einvoice.inbound.reject',
  title: 'E-Rechnung ablehnen',
  description:
    'Lehnt eine eingegangene E-Rechnung ab (doppelt, nicht an uns, keine E-Rechnung …). Die Datei bleibt archiviert.',
  llm: {
    description:
      'Lehnt eine eingegangene, noch nicht erfasste E-Rechnung mit Grund ab: duplicate (doppelt), not_addressed_to_us, not_einvoice, other (mit Notiz). Die Datei bleibt unverändert archiviert; die Ablehnung ist endgültig.',
    keywords: ['E-Rechnung ablehnen', 'doppelt', 'nicht an uns'],
  },
  input: z.strictObject({
    id: zId,
    reason: z.enum(REJECT_REASONS, { error: 'Bitte wählen Sie einen Grund.' }),
    note: zNote.nullish(),
  }),
  output: zInboundRow,
  permission: INBOUND_PROCESS,
  risk: 'write',
  assistant: 'opt-in',
})

export const inboundReparse = defineContract({
  name: 'einvoice.inbound.reparse',
  title: 'E-Rechnung erneut lesen',
  description:
    'Liest eine eingegangene, noch nicht erfasste E-Rechnung erneut – etwa nach einer neuen Version des Lesers. Die bisherige Leseversion bleibt.',
  llm: { description: 'Nur für die Oberfläche: E-Rechnung erneut lesen (neue Leseversion).', keywords: [] },
  input: z.strictObject({ id: zId }),
  output: zInboundRow,
  permission: INBOUND_PROCESS,
  risk: 'write',
  assistant: 'never',
})
